/**
 * The gym's runs, as `python -m eval.gym serve` hands them out on /gym (the dev server proxies it). Every rule —
 * which items a strategy gives, what they show, how an answer becomes a verdict — stays in Python
 * (backend/eval/gym/review); this is only the wire.
 */

export interface ShownVerdict {
  by: string;
  value: string;
  rationale: string;
}

export interface ReviewItem {
  id: string;
  episode: string;
  target: string;
  question: string;
  strategy: string;
  blind: boolean;
  shown: ShownVerdict[];
  other: string | null;
}

export interface ReviewList {
  run: string;
  strategy: string;
  by: string;
  categories: string[];
  items: ReviewItem[];
}

export interface ReviewAnswer {
  item: string;
  strategy: string;
  by: string;
  value: 'pass' | 'fail' | 'unsure';
  rationale: string;
  category: string | null;
  evidence_turns: number[];
}

/** What the review panel works on: the run, who reviews, how, the item on screen and the whole list. */
export interface ReviewContext {
  run: string;
  by: string;
  strategy: string;
  item: ReviewItem;
  list: ReviewList;
}

async function ok(res: Response): Promise<Response> {
  if (!res.ok) throw new Error(`gym ${res.status}: ${await res.text()}`);
  return res;
}

const runPath = (run: string) => `/gym/run/${encodeURIComponent(run)}`;

export async function fetchItems(run: string, strategy: string, by: string): Promise<ReviewList> {
  const q = new URLSearchParams({ strategy, by });
  return (await ok(await fetch(`${runPath(run)}/items?${q}`))).json() as Promise<ReviewList>;
}

export async function fetchRender(run: string, item: string, strategy: string, by: string): Promise<string> {
  const q = new URLSearchParams({ item, strategy, by });
  return ((await (await ok(await fetch(`${runPath(run)}/render?${q}`))).json()) as { text: string }).text;
}

export async function fetchTrajectory(run: string, episode: string): Promise<string> {
  return (await ok(await fetch(`${runPath(run)}/trajectory/${encodeURIComponent(episode)}`))).text();
}

export async function sendAnswer(run: string, answer: ReviewAnswer): Promise<void> {
  await ok(await fetch(`${runPath(run)}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(answer),
  }));
}

/** The address of another item of the same review (a reload: each item is its own recording). */
export function itemUrl(href: string, item: string | null): string {
  const url = new URL(href);
  if (item) url.searchParams.set('item', item);
  else url.searchParams.delete('item');
  return url.toString();
}
